import React, { useEffect, useRef, useState } from 'react';
import Dynamsoft from 'dwt';
import { WebTwain } from 'dwt/dist/types/WebTwain';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { Puller } from './TwScannerMenu';
import DateUtils from '../../utils/dateUtils';
import { LoadDocumentType } from '../../redux/api/load/load.model';
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import MenuItem from '@mui/material/MenuItem';
import TwNormalCamera from './TwNormalCamera';
import { ReactComponent as ScannerIcon } from '../../assets/document-scanner.svg';
import { ReactComponent as CameraIcon } from '../../assets/document-camera.svg';
import './TwScanner.scss';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useAuth from '../../providers/Auth/Auth.provider';
import { Backdrop, CircularProgress, Typography } from '@mui/material';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import {
  useUploadOrderTripDocumentByTokenMutation,
  useUploadOrderTripDocumentMutation
} from '../../redux/api/orders/ordersApi';
import { OrdersTripsDocumentParams } from '../../redux/api/orders/orders.model';
import TwScannerMobileMenu from './TwScannerMenu';
import ScannerUtils, { MOBILE_UPLOAD_OPTIONS } from '../../utils/scanner';
import { ReactComponent as DollarExpenseSvg } from '../../assets/icons/dollar-expense.svg';
import CreateOrEditOtheExpenseForm from '../../pages/OtherExpenses/CreateOrEditExpense/CreateOrEditExpense';
import { AmplitudeEvent } from '../../utils/amplitudeUtils';
import { logAmplitudeEvent } from '../../utils/amplitudeUtils';

/**
 *
 * This component has been built to work with Dynamsoft v17.3.1
 *
 */
declare global {
  interface Window {
    flutter_inappwebview: { callHandler: any };
    documentName: string | null;
  }
}

export interface TwScannerProps {
  isOpen: boolean;
  closeMenu: () => void;
  onClose: (documentUploaded: boolean, showNotification: boolean) => void;
  token?: string;
  onUploadSuccess: () => void;
  orderId?: string;
  tripId?: string;
  defaultVisibility: boolean;
  entity: 'order' | 'trip';
  entityList: { value: string; label: string }[];
  ltlEnabled: boolean;
  readableTripId?: string;
  driver?: { id: string; name: string };
  isDriverLink?: boolean;
  updateCounterTab?: () => void;
}

const TwScanner: React.FC<TwScannerProps> = ({
  isOpen,
  onClose,
  closeMenu,
  token,
  onUploadSuccess,
  orderId,
  tripId,
  defaultVisibility,
  entity,
  entityList,
  ltlEnabled,
  readableTripId,
  driver,
  isDriverLink = false,
  updateCounterTab
}): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();

  const { enableSystemCameraForDocumentUpload } = useFeatureFlags();
  const dynamsoftRef = useRef<HTMLDivElement>(null);
  const [containerId] = useState<string>('dwtcontrolContainer');
  const [DWObject, setDWObject] = useState<WebTwain>();
  const [openScanner, setOpenScanner] = useState<boolean>(false);
  const [openNewExpense, setNewExpense] = useState<boolean>(false);
  const [openScannerWithPhotoLibrary, setOpenScannerWithPhotoLibrary] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [uploadSuccessful, setUploadSuccessful] = useState<boolean>(false);
  const [option, setOption] = useState<MOBILE_UPLOAD_OPTIONS | null>(null);
  const [openNormalCamera, setOpenNormalCamera] = useState<boolean>(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [isApiCallInProgress, setIsApiCallInProgress] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { addNotification, setIsShowing } = useNotifications();
  const [flutterDoc, setFlutterDoc] = useState<File | null>(null);
  const [scannerUIWasClosed, setScannerUIWasClosed] = useState<boolean>(false);

  const [uploadOrderTripDocument] = useUploadOrderTripDocumentMutation();
  const [uploadOrderTripDocumentByToken] = useUploadOrderTripDocumentByTokenMutation();

  const handleCloseAndReset = (isSuccess: boolean): void => {
    onClose(isSuccess, true);
    setOption(null);
    setPhotos([]);
    setOpenScanner(false);
    setOpenScannerWithPhotoLibrary(false);
    setOpenNormalCamera(false);
    closeMenu();
  };

  const addIdsToFiles = (
    file: OrdersTripsDocumentParams,
    entityId: string
  ): OrdersTripsDocumentParams => {
    if (!ltlEnabled) {
      file.tripId = tripId;
      file.orderId = orderId;
    } else {
      // if uploading from order we can select trips options,
      // orderId is empty if trip id was selected from options, if no trip id was selected then we use orderId
      if (entity === 'order') {
        if (entityId === '') {
          file.tripId = '';
          file.orderId = orderId;
        } else {
          file.tripId = entityId;
          file.orderId = orderId;
        }
      }

      // uploading from a trip we can select orders options,
      // tripId is empty if order id was selected from options, if no order id was selected then we use tripId
      if (entity === 'trip') {
        if (entityId === '') {
          file.orderId = '';
          file.tripId = tripId;
        } else {
          file.orderId = entityId;
          file.tripId = tripId;
        }
      }
    }

    return file;
  };

  const handleUploadDynamsoft = async (type: LoadDocumentType, entityId: string): Promise<void> => {
    if (DWObject) {
      addNotification({
        message: 'Processing images...',
        position: 'bottomCenter',
        variant: 'loading',
        timeToShow: 10000
      });
      const totalFilesAmount = DWObject.HowManyImagesInBuffer;
      let count = 0;
      const arrayDocs: OrdersTripsDocumentParams[] = [];
      const processBlob = (index: number): void => {
        if (!DWObject) return;
        DWObject.ConvertToBlob(
          [index],
          Dynamsoft.DWT.EnumDWT_ImageType.IT_PDF,
          (blob) => {
            count++;
            const document = new File(
              [blob],
              `Mobile-scan${totalFilesAmount > 1 ? `-${count}` : ''}-${DateUtils.formatDateForScan(
                new Date()
              )}.pdf`,
              {
                type: blob.type
              }
            );

            const docWithParams: OrdersTripsDocumentParams = {
              document: document,
              type,
              visibility: defaultVisibility,
              entity: entity,
              name: document.name
            };

            const docWithParamsAndIds = addIdsToFiles(docWithParams, entityId);
            arrayDocs.push(docWithParamsAndIds);

            if (count < DWObject.HowManyImagesInBuffer) {
              processBlob(count);
            } else {
              uploadFromDynamsoft(arrayDocs);
            }
          },
          (errorCode, errorString) => {
            setUploadSuccessful(false);
            console.error('Dynamsoft Error', { errorCode, errorString });
            logRocketTrack('Upload', {
              action: 'Upload document on mobile via scanner',
              severity: 'high',
              tenant_id: userData?.tenant?.id,
              company_name: userData?.tenant?.company_name,
              email: userData?.email_address,
              user_first_name: userData?.first_name,
              user_last_name: userData?.last_name
            });
            addNotification({
              message: 'Could not upload document. Please try again',
              position: 'bottomCenter',
              variant: 'error'
            });
          }
        );
      };

      processBlob(0);
    }
  };

  const uploadFromDynamsoft = async (filesToUpload: OrdersTripsDocumentParams[]): Promise<void> => {
    // manually stopping processing images notification before showing the uploading document notification
    setIsShowing(false);

    setIsApiCallInProgress(true);
    await Promise.all(
      filesToUpload.map(async (doc) => {
        token
          ? await uploadOrderTripDocumentByToken({
              document: doc,
              token,
              dynamsoft: true
            }).unwrap()
          : await uploadOrderTripDocument({
              document: doc,
              dynamsoft: true
            }).unwrap();
      })
    )
      .then(() => {
        setUploadSuccessful(true);
        // stop showing uploading notification manually instead of waiting for timeout
        setIsShowing(false);
        setTimeout(
          () =>
            addNotification({
              message: 'Document(s) uploaded successfully',
              position: 'bottomCenter',
              variant: 'success'
            }),
          100
        );
        onUploadSuccess();
        handleCloseAndReset(true);
      })
      .catch((error) => {
        console.error({ error });
        setUploadSuccessful(false);
        setIsShowing(false);
        logRocketTrack('Upload', {
          action: 'Upload document on mobile via scanner',
          severity: 'high',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: 'Could not upload document. Please try again',
          position: 'bottomCenter',
          variant: 'error'
        });
        handleCloseAndReset(false);
      })
      .finally(() => {
        setIsApiCallInProgress(false);
        setScannerUIWasClosed(false);
      });
  };

  const handleUploadNormal = async (
    type: LoadDocumentType,
    entityId: string,
    files?: File[]
  ): Promise<void> => {
    try {
      setIsApiCallInProgress(true);

      const filesToUse = files && files.length > 0 ? files : photos;

      const documentsWithParams: OrdersTripsDocumentParams[] = filesToUse.map((file) => {
        const doc: OrdersTripsDocumentParams = {
          document: file,
          type,
          visibility: defaultVisibility,
          entity: entity,
          name: file.name
        };

        const docWithParamsAndIds = addIdsToFiles(doc, entityId);
        return docWithParamsAndIds;
      });

      await Promise.all(
        documentsWithParams.map(async (doc) => {
          token
            ? await uploadOrderTripDocumentByToken({ document: doc, token }).unwrap()
            : await uploadOrderTripDocument({
                document: doc
              }).unwrap();
        })
      )
        .then(() => {
          setIsShowing(false);
          setTimeout(
            () =>
              addNotification({
                message: 'Document(s) uploaded successfully',
                position: 'bottomCenter',
                variant: 'success'
              }),
            100
          );
          onUploadSuccess();
          handleCloseAndReset(true);
        })
        .catch((error: any) => {
          console.error({ error });

          setIsShowing(false);
          addNotification({
            message: error.message || 'Could not upload one or many documents. Please try again',
            position: 'bottomCenter',
            variant: 'error'
          });
          handleCloseAndReset(false);
        })
        .finally(() => {
          setIsApiCallInProgress(false);
        });
    } catch (error) {
      console.error('Error normal upload', error);

      addNotification({
        message: 'Error uploading document(s). Please try again',
        position: 'bottomCenter',
        variant: 'error'
      });
      handleCloseAndReset(false);
    }
  };

  const handleCloseMenu = (type: LoadDocumentType, entityId: string): void => {
    setIsMenuOpen(false);
    option === MOBILE_UPLOAD_OPTIONS.CAMERA
      ? handleUploadNormal(type, entityId)
      : ScannerUtils.isWebInFlutter()
      ? handleFlutterDocumentUpload(type, entityId)
      : handleUploadDynamsoft(type, entityId);
  };

  const handlePhotosUpload = (ph: File[]): void => {
    const filesToUpload = [...photos, ...ph];
    setPhotos(filesToUpload);

    if (filesToUpload.length === 0) {
      addNotification({
        message: 'Error: No documents to upload',
        variant: 'error'
      });
      return;
    }

    // pop up the menu to select doc type if only one photo is selected, for bulk upload, we will not show the menu
    if (filesToUpload.length === 1) {
      setIsMenuOpen(true);
    } else if (filesToUpload.length > 1) {
      // beacuse we are not showing the menu, for multiple files we will upload with the same type
      handleUploadNormal(LoadDocumentType.empty, '', filesToUpload);
    }
  };

  const handleClickScannerOption = (): void => {
    setOpenScanner(true);
    setOption(MOBILE_UPLOAD_OPTIONS.SCANNER);
    closeMenu();
  };

  const handleClickNewExpense = (): void => {
    setNewExpense(true);
    closeMenu();
  };

  const handleClickOpenPhotoLibraryScannerOption = (): void => {
    setOpenScannerWithPhotoLibrary(true);
    setOption(MOBILE_UPLOAD_OPTIONS.SCANNER);
    closeMenu();
  };

  const handleClickCameraOption = (): void => {
    setOption(MOBILE_UPLOAD_OPTIONS.CAMERA);
    closeMenu();
    setOpenNormalCamera(true);
  };

  const handleFlutterDocumentUpload = (type: LoadDocumentType, entityId: string): void => {
    try {
      if (!flutterDoc) {
        return;
      }
      const document = flutterDoc;
      setFlutterDoc(null);
      // manually stopping processing images notification before showing the uploading document notification
      setIsShowing(false);
      addNotification({
        message: 'Uploading document...',
        position: 'bottomCenter',
        variant: 'loading',
        timeToShow: 50000
      });

      const documentWithParams: OrdersTripsDocumentParams = {
        document: document,
        type,
        visibility: defaultVisibility,
        entity: entity,
        name: window.documentName ?? `Mobile-scan-${DateUtils.formatDateForScan(new Date())}.pdf`
      };

      const docWithParamsAndIds = addIdsToFiles(documentWithParams, entityId);

      setIsApiCallInProgress(true);
      const promise = token
        ? uploadOrderTripDocumentByToken({
            document: docWithParamsAndIds,
            token: token,
            dynamsoft: true
          }).unwrap()
        : uploadOrderTripDocument({
            document: docWithParamsAndIds,
            dynamsoft: true
          }).unwrap();

      promise
        .then(() => {
          // stop showing uploading notification manually instead of waiting for timeout
          setIsShowing(false);
          setTimeout(
            () =>
              addNotification({
                message: 'Document uploaded successfully',
                position: 'bottomCenter',
                variant: 'success'
              }),
            100
          );
          onUploadSuccess();

          handleCloseAndReset(true);
        })
        .catch((error) => {
          console.error({ error });
          setUploadSuccessful(false);
          setIsShowing(false);
          logRocketTrack('Upload', {
            action: 'Upload document on mobile via scanner',
            severity: 'high',
            tenant_id: userData?.tenant?.id,
            company_name: userData?.tenant?.company_name,
            email: userData?.email_address,
            user_first_name: userData?.first_name,
            user_last_name: userData?.last_name
          });
          addNotification({
            message: 'Could not upload document. Please try again',
            position: 'bottomCenter',
            variant: 'error'
          });
          handleCloseAndReset(false);
        })
        .finally(() => {
          setIsApiCallInProgress(false);
        });
    } catch (err) {
      console.error('Unable to upload doc via flutter app', err);
    }
  };

  useEffect(() => {
    window.addEventListener('message', messageEvent, false);
    return (): void => {
      window.removeEventListener('message', messageEvent);
    };
  }, []);

  const messageEvent = (event: any): void => {
    if (event.data == 'pdfNotSelected') {
      setOpenScanner(false);
      setOpenScannerWithPhotoLibrary(false);

      setIsLoading(false);
      return;
    }

    if (event.data instanceof ArrayBuffer) {
      try {
        const docJson = JSON.parse(new TextDecoder().decode(event.data));
        const name = docJson.name;

        window.documentName = name;
      } catch (e) {
        const blob = new Blob([event.data], { type: 'application/pdf' });

        const name = window.documentName;

        const document = new File(
          [blob],
          name ?? `Mobile-scan-${DateUtils.formatDateForScan(new Date())}.pdf`,
          {
            type: blob.type
          }
        );

        setFlutterDoc(document);
        window.documentName = null;
        setOpenScanner(false);
        setOpenScannerWithPhotoLibrary(false);
        setIsLoading(false);
        setIsMenuOpen(true);
      }
      return;
    }

    try {
      const data = JSON.parse(event.data);
      if (data.type == 'document') {
        const name = data.name;

        const arr = new Uint8Array(data.data);

        const blob = new Blob([arr], { type: 'application/pdf' });

        const document = new File(
          [blob],
          name ?? `Mobile-scan-${DateUtils.formatDateForScan(new Date())}.pdf`,
          {
            type: blob.type
          }
        );

        setFlutterDoc(document);
        setOpenScanner(false);
        setOpenScannerWithPhotoLibrary(false);
        setIsMenuOpen(true);
        setIsLoading(false);
      }
    } catch (e) {}
  };

  useEffect(() => {
    if (openScanner) {
      if (ScannerUtils.isWebInFlutter()) {
        setIsLoading(true);
        window.flutter_inappwebview?.callHandler?.('openScannerHandler', false);
      } else {
        Dynamsoft.DWT.Load();
      }
    } else {
      if (DWObject) {
        DWObject.UnbindViewer();
        setDWObject(undefined);
      }
      setUploadSuccessful(false);
    }
  }, [openScanner]);

  useEffect(() => {
    if (openScannerWithPhotoLibrary) {
      setIsLoading(true);
      window.flutter_inappwebview?.callHandler?.('openScannerHandler', true);
    } else {
      setUploadSuccessful(false);
    }
  }, [openScannerWithPhotoLibrary]);

  useEffect(() => {
    if (DWObject) {
      if (!DWObject.UseLocalService) {
        DWObject.BufferMemoryLimit = 3072; // for better performance
        DWObject.Addon.Camera.scanDocument(
          ScannerUtils.getVideoConfigs({
            uploadSuccessful,
            handleCloseAndReset,
            setScannerUIWasClosed
          })
        )
          .then(null, (error: any) => {
            if (error.code === -4208) {
              const browser = ScannerUtils.getBrowserName();

              let message = `${browser} not able to access camera. To use mobile scanner, please go to your phone Settings > ${browser} > Camera and allow camera access`;

              if (browser === 'No browser detection') {
                message = `Your browser is not able to access your camera. Please update your phone's settings before using Truckbase scan.`;
              }

              addNotification({
                message,
                variant: 'error',
                timeToShow: 15000,
                position: 'bottomCenter'
              });
            }
            logRocketTrack('Upload document', {
              action: 'Upload document on mobile via scanner',
              severity: 'high',
              tenant_id: userData?.tenant?.id,
              company_name: userData?.tenant?.company_name,
              email: userData?.email_address,
              user_first_name: userData?.first_name,
              user_last_name: userData?.last_name
            });
            addNotification({
              message: 'Could not load scanner. Please try again',
              position: 'bottomCenter',
              variant: 'error'
            });
            console.error('Dynamsoft Error', { error });
            handleCloseAndReset(false);
          })
          .catch((error) => {
            logRocketTrack('Upload document', {
              action: 'Upload document on mobile via scanner',
              severity: 'high',
              tenant_id: userData?.tenant?.id,
              company_name: userData?.tenant?.company_name,
              email: userData?.email_address,
              user_first_name: userData?.first_name,
              user_last_name: userData?.last_name
            });
            addNotification({
              message: 'Could not load scanner. Please try again',
              position: 'bottomCenter',
              variant: 'error'
            });
            handleCloseAndReset(false);
            console.error('Dynamsoft Error', { error });
          });
      }
    }
  }, [DWObject]);

  useEffect(() => {
    Dynamsoft.DWT.ProductKey = ScannerUtils.getDinamicWebTwainKey();
    Dynamsoft.DWT.ResourcesPath = '/dwt-resources';
    Dynamsoft.DWT.Containers = [
      {
        ContainerId: containerId,
        Width: '0px',
        Height: '0px'
      }
    ];
    Dynamsoft.DWT.UseLocalService = false;

    Dynamsoft.MSG.Init_CreatingDWT = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_AllJsLoaded = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_CheckDWT = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_CheckDWTVersion = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_CheckingLicense = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_CompilingWasm = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_ConfiguringDWT = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_DownloadingWasm = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_FireBeforeInitEvt = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_GetLicenseInfoForDWT = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_InitActiveX = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_InitDynamsoftService = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_InitH5 = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_InitWasm = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_LoadingViewerJs = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_LookingLicense = 'Loading Truckbase scanner';
    Dynamsoft.MSG.Init_SetLicenseForDWT = 'Loading Truckbase scanner';

    if (ScannerUtils.isOs()) {
      window.OffscreenCanvas = null;
    }

    Dynamsoft.DWT.RegisterEvent('OnWebTwainReady', () => {
      setDWObject(Dynamsoft.DWT.GetWebTwain(containerId));
    });

    Dynamsoft.DWT.CustomizableDisplayInfo.loaderBarClassName = 'tw-scanner';
    Dynamsoft.DWT.CustomizableDisplayInfo.loaderBarSource = '/spin.svg';

    return (): void => {
      DWObject?.UnbindViewer();
      setDWObject(undefined);
    };
  }, []);

  useEffect(() => {
    if (DWObject && scannerUIWasClosed) {
      const amountOfFiles = DWObject.HowManyImagesInBuffer;

      if (amountOfFiles === 0) {
        addNotification({
          message: 'No document scanned',
          variant: 'error'
        });
        handleCloseAndReset(false);
      } else if (amountOfFiles === 1) {
        setIsMenuOpen(true);
      } else if (amountOfFiles > 1) {
        logAmplitudeEvent(AmplitudeEvent.DYNAMSOFT_SCAN);
        handleUploadDynamsoft(LoadDocumentType.empty, '');
      }
    }
  }, [DWObject, scannerUIWasClosed]);

  const handleCloseNewExpense = (changed: boolean): void => {
    setNewExpense(false);
    changed && updateCounterTab && updateCounterTab();
  };

  return (
    <>
      <SwipeableDrawer
        anchor="bottom"
        open={isOpen}
        disableSwipeToOpen
        onClose={closeMenu}
        onOpen={(): void => undefined}
        PaperProps={{
          sx: {
            borderTopLeftRadius: '20px',
            borderTopRightRadius: '20px'
          }
        }}
      >
        <Box
          sx={{
            paddingTop: '56px',
            paddingBottom: '22px',
            paddingLeft: '11px',
            paddingRight: '11px',
            height: '100%',
            overflow: 'auto'
          }}
          onClick={(e): void => {
            e.stopPropagation();
          }}
        >
          <Puller />

          <MenuItem onClick={handleClickScannerOption} className="tw-scanner__upload-option">
            <ScannerIcon />
            Open document scanner
          </MenuItem>
          {enableSystemCameraForDocumentUpload && (
            <MenuItem onClick={handleClickCameraOption} className="tw-scanner__upload-option">
              <CameraIcon />
              Upload photos
            </MenuItem>
          )}
          {entity === 'trip' && (
            <MenuItem onClick={handleClickNewExpense} className="tw-scanner__upload-option">
              <DollarExpenseSvg />
              New expense
            </MenuItem>
          )}
        </Box>
      </SwipeableDrawer>
      {option === MOBILE_UPLOAD_OPTIONS.SCANNER && <div id={containerId} ref={dynamsoftRef}></div>}
      {option === MOBILE_UPLOAD_OPTIONS.CAMERA && (
        <TwNormalCamera
          open={openNormalCamera}
          onFileUpload={handlePhotosUpload}
          close={(): void => {
            onClose(false, false);
            setOption(null);
            setPhotos([]);
            setOpenScanner(false);
            setOpenScannerWithPhotoLibrary(false);
            setOpenNormalCamera(false);
          }}
        />
      )}
      <TwScannerMobileMenu
        isOpen={isMenuOpen}
        onClose={handleCloseMenu}
        entityList={entityList}
        entityToSelect={entity === 'order' ? 'trip' : 'order'}
        ltlEnabled={ltlEnabled}
      />

      {openNewExpense && entity === 'trip' && (
        <CreateOrEditOtheExpenseForm
          open={openNewExpense}
          onClose={handleCloseNewExpense}
          otherExpenseId={null}
          tripId={tripId}
          readableTripId={readableTripId}
          isDriverLink={isDriverLink}
          driver={driver}
          token={token}
          isView={false}
        />
      )}

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 10
        }}
        open={isApiCallInProgress}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            padding: '20px',
            borderRadius: '10px'
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" color="inherit">
            Please hold, uploading...
          </Typography>
        </Box>
      </Backdrop>

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 10
        }}
        open={isLoading}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            padding: '20px',
            borderRadius: '10px'
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" color="inherit">
            Please hold, processing file...
          </Typography>
        </Box>
      </Backdrop>
    </>
  );
};

export default TwScanner;
